import { PartnerRfpRejectReasonConstant } from "./constant/PartnerRfpRejectReasonConstant";
import { PartnerRfpStatusConstant } from "./constant/PartnerRfpStatusConstant";
import { PartnerRfpRequestDTO } from "./dto/PartnerRfpRequestDTO";
import { AccountService } from "../../services/AccountService";
import { AuthoritiesConstants } from "../AuthoritiesModel/constants/AuthoritiesConstants";
import { RouteUtils } from "../../routes/RouteUtils";

// Search criteria <-> URL query string. Array fields are written as repeated params (?statusList=1&statusList=2).
// Every other field not listed here is a plain string (id, userId, dates as YYYY-MM-DD, free text...).
const NUMBER_ARRAY_FIELDS = ['businessTypeIDs', 'regions', 'rejectReasons', 'operatorIds', 'affiliateIds', 'statusList', 'statusNotIn'];
const STRING_ARRAY_FIELDS = ['zipCodes'];
const NUMBER_FIELDS = ['status', 'cannotContact'];
const BOOLEAN_FIELDS = ['isLeadPlus', 'isPriority'];

const formatParamValue = (v: unknown): string =>
    v instanceof Date
        ? `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`
        : String(v);

export const PartnerRfpUtils = {
    /** Serializes lead-search criteria to URL params; empty values are omitted. */
    toSearchParams: (criteria: Partial<PartnerRfpRequestDTO>): URLSearchParams => {
        const params = new URLSearchParams();
        Object.keys(new PartnerRfpRequestDTO()).forEach((key) => {
            const value = (criteria as Record<string, unknown>)[key];
            if (value == null || value === '') return;
            if (Array.isArray(value)) {
                value.forEach((item) => params.append(key, formatParamValue(item)));
            } else {
                params.set(key, formatParamValue(value));
            }
        });
        return params;
    },

    /** Parses lead-search criteria from URL params; unknown params (e.g. table pagination) are ignored. */
    fromSearchParams: (params: URLSearchParams): Partial<PartnerRfpRequestDTO> => {
        const criteria: Record<string, unknown> = {};
        Object.keys(new PartnerRfpRequestDTO()).forEach((key) => {
            const values = params.getAll(key).filter((v) => v !== '');
            if (values.length === 0) return;
            if (NUMBER_ARRAY_FIELDS.includes(key)) {
                criteria[key] = values.map(Number).filter(Number.isFinite);
            } else if (STRING_ARRAY_FIELDS.includes(key)) {
                criteria[key] = values;
            } else if (NUMBER_FIELDS.includes(key)) {
                const n = Number(values[0]);
                if (Number.isFinite(n)) criteria[key] = n;
            } else if (BOOLEAN_FIELDS.includes(key)) {
                criteria[key] = values[0] === 'true';
            } else {
                criteria[key] = values[0];
            }
        });
        return criteria as Partial<PartnerRfpRequestDTO>;
    },

    /** Link to the lead search results page for the given criteria. */
    getSearchResultLink: (criteria: Partial<PartnerRfpRequestDTO>): string => {
        const qs = PartnerRfpUtils.toSearchParams(criteria).toString();
        return qs ? `/partner-rfp-search-result?${qs}` : '/partner-rfp-search-result';
    },

    /** Whether the operator uses the new React lead pages (PARTNER_RFP_NEW_PAGES) rather than the legacy ones. */
    usesNewPages: (): boolean => AccountService.hasAuthority([AuthoritiesConstants.PARTNER_RFP_NEW_PAGES]),

    /** Edit page of a lead: React leads-edit with PARTNER_RFP_NEW_PAGES, the legacy page otherwise. */
    getEditLink: (partnerRfpId: number): string =>
        PartnerRfpUtils.usesNewPages()
            ? `/leads-edit/${partnerRfpId}`
            : `${RouteUtils.getBackofficeServletBasename()}/bo_partner_rfp_detail_edit?rfp.id=${partnerRfpId}`,

    translateStatus: (status: number, t: Function) => {
        switch (status) {
            case PartnerRfpStatusConstant.WITHHELD:
                return t("partnerRfp.status.withheld");
            case PartnerRfpStatusConstant.REJECTED:
                return t("Rejected");
            case PartnerRfpStatusConstant.ACCEPTED:
                return t("Sent");
            case PartnerRfpStatusConstant.DELETED_AFTER_REJECTION:
                return t("Deleted after rejection");
            default:
                return t("Unknown");
        }
    },

    translateRejectReason: (reason: number, t: Function) => {
        switch (reason) {
            case PartnerRfpRejectReasonConstant.WRONG_CATEGORY:
                return t("Wrong category");
            case PartnerRfpRejectReasonConstant.TOO_SMALL:
                return t("Too small");
            case PartnerRfpRejectReasonConstant.CANNOT_CONTACT:
                return t("Cannot contact");
            case PartnerRfpRejectReasonConstant.UNSERIOUS_BUYER:
                return t("Unserious buyer");
            case PartnerRfpRejectReasonConstant.WRONG_COUNTRIES:
                return t("Wrong countries");
            case PartnerRfpRejectReasonConstant.DUPLICATE:
                return t("Duplicate");
            case PartnerRfpRejectReasonConstant.OTHER_BO:
                return t("Other");
            case PartnerRfpRejectReasonConstant.TEST:
                return t("Test");
            case PartnerRfpRejectReasonConstant.DONT_UNDERSTAND:
                return t("Doesn't understand");
            case PartnerRfpRejectReasonConstant.NO_SUPPLIER:
                return t("No supplier");
            case PartnerRfpRejectReasonConstant.SPAM:
                return t("Spam");
            case PartnerRfpRejectReasonConstant.NOT_COMPANY:
                return t("Not a company");
            case PartnerRfpRejectReasonConstant.OUT_FILTER:
                return t("Out of filter");
            case PartnerRfpRejectReasonConstant.NOT_BOBEX_COUNTRIES:
                return t("Not a Bobex country");
            case PartnerRfpRejectReasonConstant.NO_RELAUNCH_NEEDED:
                return t("No relaunch needed");
            case PartnerRfpRejectReasonConstant.TOO_LATE:
                return t("Too late");
            case PartnerRfpRejectReasonConstant.NO_MEETING:
                return t("No meeting");
            case PartnerRfpRejectReasonConstant.BAD_PUB:
                return t("Bad publisher");
            case PartnerRfpRejectReasonConstant.VBK_RETURN:
                return t("VBK return");
            default:
                return t("Unknown");
        }
    },

    // A request with no user-facing filter would scan the whole lead table and is rejected by the backend
    // (PartnerRfpServiceImpl empty-search guard). Shared by the search form (disable submit) and the results
    // page (show a message instead of firing a doomed request). Keep in sync with the backend field list.
    isEmptySearch: (d: PartnerRfpRequestDTO): boolean => {
        const noStr = (s?: string | null) => s == null || s === '';
        const noArr = (a?: unknown[] | null) => a == null || a.length === 0;
        return !d.id
                && noStr(d.projectName) && noStr(d.name)
                && d.submissionDateStart == null && d.submissionDateEnd == null
                && d.creationDateStart == null && d.creationDateEnd == null
                && noArr(d.businessTypeIDs)
                && noStr(d.telNumber) && noStr(d.gstNumber)
                && noStr(d.address) && noStr(d.address2)
                && noStr(d.email) && noStr(d.description)
                && d.status == null
                && noStr(d.clickId)
                && (d.languageID == null || d.languageID === '')
                && noStr(d.utmSource)
                && noArr(d.regions) && noArr(d.statusNotIn)
                && noStr(d.dateStartCreationYYYYMMDD) && noStr(d.dateEndCreationYYYYMMDD)
                && (d.dateSubmissionYYYYMM == null || d.dateSubmissionYYYYMM === '')
                && (d.userId == null || d.userId === '')
                && noArr(d.rejectReasons)
                && noStr(d.remoteId) && noStr(d.remoteSource)
                && noArr(d.zipCodes) && noArr(d.operatorIds) && noArr(d.affiliateIds) && noArr(d.statusList)
                && d.cannotContact == null
                && d.isLeadPlus == null && d.isPriority == null;
    }

};

